import { Box, Image, Modal, ModalContent, ModalOverlay, useDisclosure } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { FontFamily } from '@common/theming/Fonts';
import React from 'react';
import { PROPERTY_STATUS } from 'src/apps/invest/config';
import { Keyboard, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import './styles.css';

type GalleryProps = {
  children?: React.ReactNode;
};

export const Gallery: React.FC<GalleryProps> = () => {
  const { property } = usePropertiesModel();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [initialSlide, setInitialSlide] = React.useState(0);

  // Pad the array with the first image if there are less than 4 images or pad with empty objects
  const images = Object.assign(
    new Array(4).fill(
      property?.sfr?.info?.images && property.sfr.info.images.length > 0
        ? property.sfr.info.images[0]
        : {
            description: 'Image Not Available',
            src: '/assets/gfx/logo-black.svg',
            title: 'Image Not Available',
          },
    ),
    property?.sfr?.info.images,
  );

  return (
    <Box
      display="flex"
      flexDirection={['column', 'column', 'row']}
      height={['325px', '525px']}
      gap="8px"
      onClick={onOpen}>
      <Box
        cursor={'pointer'}
        backgroundImage={images[0].url}
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
        backgroundPosition={'center center'}
        borderRadius={['10px 10px 0px 0px', '10px 10px 0px 0px', '10px 0px 0px 10px']}
        opacity={property?.sfr?.status.status === 'SOLD_OUT' ? 0.6 : 1}
        onClick={() => setInitialSlide(0)}
        w="100%"
        h="100%">
        {property?.sfr?.status.status && (
          <Box
            alignItems={'center'}
            justifyContent="center"
            display="flex"
            bgColor={PROPERTY_STATUS[property?.sfr?.status.status][3] ?? 'green.500'}
            color={PROPERTY_STATUS[property?.sfr?.status.status][4] ?? 'white'}
            borderRadius={'6px'}
            fontSize="12px"
            textTransform={'uppercase'}
            fontFamily={FontFamily.HELVETICA_NOW_BOLD}
            px="8px"
            py="2px"
            position="absolute"
            top="15px"
            right="15px">
            <Icon
              icon={PROPERTY_STATUS[property?.sfr?.status.status][1] ?? 'bolt'}
              mr="8px"
              variant={PROPERTY_STATUS[property?.sfr?.status.status][2] ?? 'solid'}
            />
            {PROPERTY_STATUS[property.sfr?.status.status][0] ?? 'Featured'}
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        gap="8px"
        flexDirection={['row', 'row', 'column']}
        width={['100%', '100%', 'auto']}
        height={['170px', '170px', 'auto']}>
        <Box
          cursor={'pointer'}
          backgroundImage={images[1].url}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          backgroundPosition={'center center'}
          borderRadius={['0px 0px 0px 10px', '0px 0px 0px 10px', '0px 10px 0px 0px']}
          opacity={property?.sfr?.status.status === 'SOLD_OUT' ? 0.6 : 1}
          flex="1"
          onClick={() => setInitialSlide(1)}
          w={['auto', 'auto', '200px']}></Box>
        <Box
          cursor={'pointer'}
          backgroundImage={images[2].url}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          backgroundPosition={'center center'}
          flex="1"
          onClick={() => setInitialSlide(2)}
          opacity={property?.sfr?.status.status === 'SOLD_OUT' ? 0.6 : 1}
          w={['auto', 'auto', '200px']}></Box>
        <Box
          cursor={'pointer'}
          backgroundImage={images[3].url}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          backgroundPosition={'center center'}
          borderRadius={['0px 0px 10px 0px']}
          flex="1"
          onClick={() => setInitialSlide(3)}
          opacity={property?.sfr?.status.status === 'SOLD_OUT' ? 0.6 : 1}
          w={['auto', 'auto', '200px']}></Box>
      </Box>
      <Modal isCentered isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(10px)" />
        <ModalContent bg="transparent" boxShadow={'none'}>
          <Box>
            <Swiper
              initialSlide={initialSlide}
              keyboard={true}
              loop={true}
              modules={[Keyboard, Pagination, Navigation]}
              navigation={true}
              pagination={{ clickable: true }}
              slidesPerView={1}
              spaceBetween={30}
              style={{
                backgroundColor: 'transparent',
              }}>
              {property?.sfr?.info?.images?.map((image, index) => (
                <SwiperSlide
                  key={`image-${index}`}
                  style={{
                    alignSelf: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                  }}>
                  <Image src={image.url} minH={['auto', 'auto', 'auto', '500px']} maxH={'80vh'} maxW="100%" />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </ModalContent>
      </Modal>
    </Box>
  );
};
